import { useMemo } from 'react';

import { EMPTY_NODES } from '../consts';
import { type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';
import { useCanvasTokenNodes } from '../store/useCanvasTokenNodeStore';
import { useChatComposerNodes } from '../store/useChatComposerNodeStore';
import { useChatHistoryNodeStore, useChatHistoryNodes } from '../store/useChatHistoryNodeStore';
import { useMediaMapNodes } from '../store/useChatStore';
import { mergeCanvasTokenNodes } from '../store/utils/mergeCanvasTokenNodes';

/**
 * Combines canvas token nodes from the canvas store, chat history store,
 * composer node store, and mediaMap from agent state, deduplicating by media URL.
 * Priority: canvas > chat history > composer > mediaMap > inactive (lowest).
 * Inactive nodes are included so that [media:id] references to deleted nodes
 * still resolve when rendering token chips in chat history.
 */
export function useMergedCanvasTokenNodes(): Record<string, CanvasTokenNode> {
  const canvasNodes = useCanvasTokenNodes();
  const chatHistoryNodes = useChatHistoryNodes();
  const composerNodes = useChatComposerNodes();
  const mediaMapNodes = useMediaMapNodes();
  const inactiveNodes = useChatHistoryNodeStore((state) =>
    Object.keys(state.inactiveNodes).length > 0 ? state.inactiveNodes : EMPTY_NODES,
  );

  return useMemo(() => {
    const merged = mergeCanvasTokenNodes(canvasNodes, chatHistoryNodes, composerNodes);
    // mediaMap under merged (live sources win), inactive at lowest priority
    const result = { ...inactiveNodes, ...mediaMapNodes, ...merged };

    return Object.keys(result).length > 0 ? result : EMPTY_NODES;
  }, [canvasNodes, chatHistoryNodes, composerNodes, mediaMapNodes, inactiveNodes]);
}
